window.customElements.define(
  'custom-variant-picker-toggle',
  class CustomVariantPickerToggle extends window.HTMLElement {
    connectedCallback() {
      this.initDOMElements();
      this.initEventListeners();
    }

    disconnectedCallback() {
      this.removeEventListeners();
    }

    // Initialize DOM elements
    initDOMElements() {
      this.form = this.querySelector('form');
      this.addToCartPrice = this.querySelector('#add-to-cart-price');
      this.addToCartCompareAtPrice = this.querySelector('#add-to-cart-compare-at-price');
      this.errorMessage = this.querySelector('#error-message');
      this.addToCartButton = this.querySelector('#add-to-cart');
      this.notifyMeButton = this.querySelector('#notify-me-button');
      this.oneTimeSwatch = this.querySelector('#swatch-item-one-time-purchase');
      this.subscribeSwatch = this.querySelector('#swatch-item-subscribe-and-save');
      this.variants = this.form ? this.form.querySelectorAll('.custom-variant-input') : [];
      this.savings = this.querySelectorAll('#savings');
      this.savingsWrapper = this.querySelectorAll('#savings-wrapper');
      this.optionUnitPriceWrapper = this.querySelectorAll('#option-unit-price-wrapper');
      this.optionUnitPrices = this.querySelectorAll('#option-unit-price');
      this.pauseOrCancelAnytimeText = this.querySelector('#pause-or-cancel-anytime');

      // store bound handlers so removeEventListeners can remove the exact same functions
      this._boundHandleChange = this.handleChange.bind(this);
      this._boundHandleSubmit = this.handleAddToCartSubmit.bind(this);
      this._boundOneTimeClick = this.handleChange.bind(this);
      this._boundSubscribeClick = this.handleChange.bind(this);
      this._boundStickyListener = this.handleStickyAddToCartChange.bind(this);
    }

    // Event Listeners
    initEventListeners() {
      if (this.form) {
        this.form.addEventListener('change', this._boundHandleChange);
        this.form.addEventListener('submit', this._boundHandleSubmit);
      }
      if (this.oneTimeSwatch) this.oneTimeSwatch.addEventListener('click', this._boundOneTimeClick);
      if (this.subscribeSwatch) this.subscribeSwatch.addEventListener('click', this._boundSubscribeClick);

      // Listen for sticky add to cart changes
      window.addEventListener('custom-sticky-add-to-cart:change', this._boundStickyListener);
    }

    removeEventListeners() {
      if (this.form) {
        this.form.removeEventListener('change', this._boundHandleChange);
        this.form.removeEventListener('submit', this._boundHandleSubmit);
      }
      if (this.oneTimeSwatch) this.oneTimeSwatch.removeEventListener('click', this._boundOneTimeClick);
      if (this.subscribeSwatch) this.subscribeSwatch.removeEventListener('click', this._boundSubscribeClick);

      // Remove sticky add to cart listener
      window.removeEventListener('custom-sticky-add-to-cart:change', this._boundStickyListener);
    }

    // Event Handlers
    handleStickyAddToCartChange(event) {
      const { variant } = event.detail;

      if (!variant.variantId) return;
      const variantInput = this.form.querySelector(`input[type="radio"][value="${variant.variantId}"]`);

      if (!variantInput) return;
      variantInput.checked = true;

      const currentVariantData = this.getCurrentVariantData();
      this.handleUpdateUI(currentVariantData);
      this.handleUpdateUrl(currentVariantData);
    }

    handleChange(event) {
      const currentVariantData = this.getCurrentVariantData();

      this.handleUpdateUI(currentVariantData, event.target);
      this.handleUpdateUrl(currentVariantData);

      this.dispatchEvent(
        new CustomEvent('custom-variant-picker:change', {
          detail: {
            variant: {
              available: currentVariantData.available,
              variantId: currentVariantData.variantId,
              sellingPlanId: currentVariantData.sellingPlanId,
              price: currentVariantData.variantPrice,
              compareAtPrice: currentVariantData.variantCompareAtPrice,
              subscriptionPrice: currentVariantData.variantSubscriptionPrice,
            },
            element: event.target,
          },
          bubbles: true,
        })
      );
    }

    async handleAddToCartSubmit(event) {
      event.preventDefault();
      const currentVariantData = this.getCurrentVariantData();
      const cartItems = this.buildCartItems(currentVariantData); // array of items

      try {
        this.setLoadingState(true);
        await this.addToCart(cartItems); // addToCart accepts array now
        this.dispatchCartRefresh();
      } catch (error) {
        this.handleAddToCartError(error);
      } finally {
        this.setLoadingState(false);
      }
    }

    // Build cart items — returns an array of items (one or more)
    buildCartItems(currentVariantData) {
      // Decide selling plan ('' when one-time)
      const sellingPlan = this.determineSellingPlan(currentVariantData);

      // If selling plan is present and we have sub IDs, prepare two items:
      //  - data-variant-id-sub WITHOUT selling_plan
      //  - data-variant-id-sub-gel WITH selling_plan (if provided)
      if (sellingPlan && (currentVariantData.variantIdSub || currentVariantData.variantId)) {
        const items = [];

        // sub: explicit data-variant-id-sub if present, otherwise fallback to current variant id
        const subId = currentVariantData.variantIdSub || currentVariantData.variantId;
        items.push({
          id: subId,
          quantity: 1,
         ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
        });

        // gel: include selling_plan if gel id exists
    /*    if (currentVariantData.variantIdSubGel) {
          items.push({
            id: currentVariantData.variantIdSubGel,
            quantity: 1,
            ...(sellingPlan ? { selling_plan: sellingPlan } : {}),
          });
        }*/

        return items;
      }

      // Default: single item (one-time or no special sub IDs)
      const singleItem = {
        id: currentVariantData.variantId,
        quantity: 1,
      };
      if (sellingPlan) {
        singleItem.selling_plan = sellingPlan;
      }
      return [singleItem];
    }

    determineSellingPlan(currentVariantData) {
      const activePaymentOption = this.getActivePaymentOption();
      return activePaymentOption === 'one-time' ? '' : currentVariantData.sellingPlanId;
    }

    setLoadingState(isLoading) {
      this.form && this.form.classList.toggle('is-loading', isLoading);
    }

    handleAddToCartError(error) {
      console.error('Failed to add to cart:', error);
      this.showErrorMessage(error.message || 'Failed to add to cart');
    }

    showErrorMessage(message) {
      if (this.errorMessage) {
        this.errorMessage.textContent = message;
      }
      this.errorMessage && this.errorMessage.classList.remove('hidden');

      setTimeout(() => {
        this.errorMessage && this.errorMessage.classList.add('hidden');
      }, 3000);
    }

    // UI Update Methods
    handleUpdateUI(currentVariantData, eventTarget = null) {
      this.updateButtonVisibility(currentVariantData);
      this.updatePriceDisplay();

      if (eventTarget) {
        this.handleSwatchClick(eventTarget);
      }
    }

    updateButtonVisibility(currentVariantData) {
      if (this.addToCartButton) {
        this.addToCartButton && this.addToCartButton.classList.toggle('hidden', !currentVariantData.available);
      }

      if (this.notifyMeButton) {
        this.notifyMeButton && this.notifyMeButton.classList.toggle('hidden', currentVariantData.available);
      }
    }

    updatePriceDisplay() {
      const activePaymentOption = this.getActivePaymentOption();
      const activeVariantInput = this.getActiveVariantInput();

      if (!activeVariantInput) return;

      if (activePaymentOption === 'one-time') {
        this.updateOneTimePrices(activeVariantInput);
        this.pauseOrCancelAnytimeText && this.pauseOrCancelAnytimeText.classList.add('hidden');
      } else if (activePaymentOption === 'subscription') {
        this.updateSubscriptionPrices(activeVariantInput);
        this.pauseOrCancelAnytimeText && this.pauseOrCancelAnytimeText.classList.remove('hidden');
      }
    }

    updateOneTimePrices(activeVariantInput) {
      const { variantPrice, variantCompareAtPrice } = activeVariantInput.dataset;

      this.setMainPrice(variantPrice);
      this.setCompareAtPrice(variantCompareAtPrice, variantPrice);
    }

    updateSubscriptionPrices(activeVariantInput) {
      const { variantSubscriptionPrice, variantCompareAtPrice } = activeVariantInput.dataset;

      this.setMainPrice(variantSubscriptionPrice);
      this.setCompareAtPrice(variantCompareAtPrice, variantSubscriptionPrice);
    }

    setMainPrice(price) {
      if (this.addToCartPrice) {
        this.addToCartPrice.textContent = price;
      }
    }

    setCompareAtPrice(compareAtPrice, mainPrice) {
      if (!this.addToCartCompareAtPrice) return;

      this.addToCartCompareAtPrice.textContent = compareAtPrice;

      if (compareAtPrice === mainPrice) {
        this.addToCartCompareAtPrice.classList.add('hidden');
      } else {
        this.addToCartCompareAtPrice.classList.remove('hidden');
      }
    }

    handleSwatchClick(eventTarget) {
      if (!eventTarget) return;
      if (eventTarget.id === 'swatch-item-one-time-purchase') {
        this.activateOneTimeSwatch();
      } else if (eventTarget.id === 'swatch-item-subscribe-and-save') {
        this.activateSubscriptionSwatch();
      }
    }

    activateOneTimeSwatch() {
      this.updateSwatchState('one-time');
      this.updateVariantOptions('one-time');
      this.updatePriceDisplay();
    }

    activateSubscriptionSwatch() {
      this.updateSwatchState('subscription');
      this.updateVariantOptions('subscription');
      this.updatePriceDisplay();
    }

    updateSwatchState(paymentType) {
      if (paymentType === 'one-time') {
        this.oneTimeSwatch && this.oneTimeSwatch.classList.add('active');
        this.subscribeSwatch && this.subscribeSwatch.classList.remove('active');
      } else {
        this.oneTimeSwatch && this.oneTimeSwatch.classList.remove('active');
        this.subscribeSwatch && this.subscribeSwatch.classList.add('active');
      }
    }

    updateVariantOptions(paymentType) {
      const variantInputs = this.form ? this.form.querySelectorAll('input.custom-variant-input') : [];

      Array.from(variantInputs).forEach((variantInput, index) => {
        if (paymentType === 'one-time') {
          this.updateOneTimeVariantOption(variantInput, index);
        } else {
          this.updateSubscriptionVariantOption(variantInput, index);
        }
      });
    }

    updateOneTimeVariantOption(variantInput, index) {
      const { variantUnitPrice, variantSavings } = variantInput.dataset;

      this.setOptionUnitPrice(index, variantUnitPrice);
      this.setOptionSavings(index, variantSavings);
    }

    updateSubscriptionVariantOption(variantInput, index) {
      const { variantSubscriptionUnitPrice, variantSubscriptionSavings } = variantInput.dataset;

      this.setOptionUnitPrice(index, variantSubscriptionUnitPrice);
      this.setOptionSavings(index, variantSubscriptionSavings);
    }

    setOptionUnitPrice(index, price) {
      if (this.optionUnitPrices[index]) {
        this.optionUnitPrices[index].textContent = price;
      }
    }

    setOptionSavings(index, savings) {
      if (savings) {
        this.savingsWrapper[index] && this.savingsWrapper[index].classList.remove('hidden');
        this.optionUnitPriceWrapper[index] && this.optionUnitPriceWrapper[index].classList.remove('hidden');
      } else {
        this.savingsWrapper[index] && this.savingsWrapper[index].classList.add('hidden');
        this.optionUnitPriceWrapper[index] && this.optionUnitPriceWrapper[index].classList.add('hidden');
        return;
      }

      if (this.savings[index]) {
        this.savings[index].textContent = savings;
      }
    }

    getActiveVariantInput() {
      return this.form ? this.form.querySelector('input[type="radio"]:checked') : null;
    }

    handleUpdateUrl(currentVariantData) {
      if (!currentVariantData || !currentVariantData.variantId) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', currentVariantData.variantId);
      window.history.replaceState({}, '', url);
    }

    // addToCart now accepts an array of items OR a single item (keeps compatibility)
    async addToCart(items) {
      // items is expected to be an array of item objects
      const body = {
        items: Array.isArray(items) ? items : [items],
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to add to cart' }));
        throw new Error(error.message || 'Add to cart failed');
      }

      const result = await response.json();
      return result;
    }

    dispatchCartRefresh() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('variant:add'));
    }

    // Utility Methods
    getCurrentVariantData() {
      const currentVariant = this.form ? this.form.querySelector('input[type="radio"]:checked') : null;
      if (!currentVariant) {
        return {
          variantId: null,
          sellingPlanId: '',
          variantPrice: null,
          variantCompareAtPrice: null,
          variantSubscriptionPrice: null,
          available: false,
          variantIdSub: null,
          variantIdSubGel: null,
        };
      }

      const {
        available,
        variantId,
        sellingPlanId,
        variantPrice,
        variantCompareAtPrice,
        variantSubscriptionPrice,
        variantIdSub,
        variantIdSubGel,
      } = currentVariant.dataset;

      return {
        variantId,
        sellingPlanId,
        variantPrice,
        variantCompareAtPrice,
        variantSubscriptionPrice,
        available,
        variantIdSub: variantIdSub || null,
        variantIdSubGel: variantIdSubGel || null,
      };
    }

    getActivePaymentOption() {
      if (this.oneTimeSwatch && this.oneTimeSwatch.classList.contains('active')) {
        return 'one-time';
      }

      if (this.subscribeSwatch && this.subscribeSwatch.classList.contains('active')) {
        return 'subscription';
      }

      // default fallback
      return 'one-time';
    }
  }
);
